import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDatabase, FiClock, FiPhone, FiStar, FiMic, FiShoppingCart, FiArrowRight } = FiIcons;

function Solutions() {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const solutions = [
    { icon: FiDatabase, title: 'Database Reactivation', description: 'Pull fresh sales from leads you\'ve already paid for and haven\'t bought, using conversational AI', color: 'from-blue-500 to-cyan-500' },
    { icon: FiClock, title: 'Speed To Lead', description: 'Stop losing sales to competitors who are faster to the phone, by getting AI to work your leads 24/7', color: 'from-green-500 to-emerald-500' },
    { icon: FiPhone, title: 'Out Of Hours', description: 'Stop \'Out of Hours\' leads going unanswered and wasting time each morning on unqualified prospects with AI', color: 'from-purple-500 to-violet-500' },
    { icon: FiStar, title: 'Google Reviews', description: 'Improve business credibility and customer opinion replying to reviews without spending man-hours using AI', color: 'from-yellow-500 to-orange-500' },
    { icon: FiMic, title: 'Voice', description: 'Use AI to handle inbound calls to redirect leads, take information and book / rearrange appointments', color: 'from-pink-500 to-rose-500' },
    { icon: FiShoppingCart, title: 'Abandoned Cart', description: 'Secure sales that would have otherwise been lost to the void by following up with users who left before purchasing', color: 'from-indigo-500 to-blue-500' }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-orange-50 relative overflow-hidden">
      {/* Background Effects (without dots) */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-600 px-6 py-2 rounded-full font-semibold mb-6">
            <span>Our Solutions</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
            We've got you covered
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our comprehensive AI solutions address every aspect of your sales process
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.title}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              className="group"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full relative overflow-hidden">
                {/* Background gradient on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${solution.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                
                <div className="relative">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${solution.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <SafeIcon icon={solution.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-orange-500 transition-colors duration-300">
                    {solution.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {solution.description}
                  </p>
                  
                  {/* Animated underline */}
                  <div className="relative h-1 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: hoveredIndex === index ? '100%' : '0%' }}
                      transition={{ duration: 0.3 }}
                      className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
                    />
                  </div>
                  
                  {/* Arrow on hover */}
                  <div className={`absolute top-8 right-8 transition-all duration-300 ${hoveredIndex === index ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-orange-500" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Solutions;